import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Shield, Lock, Eye, Edit2, Search, Settings, Plus, X, Save, Trash2, Home, ChevronRight } from 'lucide-react';
import { PERMISSION_LABELS } from '@/config/permissions';
import { useToast } from '@/contexts/ToastContext';
import { permissionModuleService } from '@/services/permission-module.service';
import { useAuthStore } from '@/store/authStore';
import { confirmDelete as confirmDeleteDialog, showDeleteSuccess, showDeleteError } from '@/utils/deleteConfirmation';

interface ModuleConfig {
  path: string;
  name: string;
  icon: React.ElementType;
  permissions: string[];
  category: string;
  _id?: string; // ID for custom modules from database
}

export function RolesManagement() {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuthStore();
  const userPermissions = currentUser?.permissions || [];
  const canEdit = userPermissions.includes('permissions_management_edit');
  const canDelete = userPermissions.includes('permissions_management_delete');

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [isAddingModule, setIsAddingModule] = useState(false);
  const [editingModuleId, setEditingModuleId] = useState<string | null>(null);

  // Fetch custom modules from database
  const { data: customModules = [], isLoading } = useQuery({
    queryKey: ['permission-modules'],
    queryFn: async () => {
      const token = localStorage.getItem('accessToken') || '';
      const modules = await permissionModuleService.getAllModules(token, { isActive: true });
      return modules.map(m => ({
        name: m.name,
        path: m.path,
        category: m.category,
        icon: Settings,
        permissions: m.permissions,
        _id: m._id
      }));
    },
  });

  const [newModule, setNewModule] = useState({
    name: '',
    path: '',
    category: '',
    hasView: false,
    hasEdit: false,
    hasDelete: false
  });

  const [editModule, setEditModule] = useState({
    name: '',
    path: '',
    category: '',
    hasView: false,
    hasEdit: false,
    hasDelete: false
  });

  // All modules come from database (permissionmodules table)
  const allModules = customModules;

  const categories = ['all', ...Array.from(new Set(allModules.map(m => m.category)))];

  const filteredModules = allModules.filter(module => {
    const matchesSearch = module.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || module.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Create module mutation
  const createMutation = useMutation({
    mutationFn: async (data: { name: string; path: string; category: string; permissions: string[] }) => {
      const token = localStorage.getItem('accessToken') || '';
      return await permissionModuleService.createModule(data, token);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['permission-modules'] });
      showToast('success', 'Module Added', `${data.name} has been added successfully`);
      setNewModule({
        name: '',
        path: '',
        category: '',
        hasView: false,
        hasEdit: false,
        hasDelete: false
      });
      setIsAddingModule(false);
    },
    onError: (error: any) => {
      showToast('error', 'Error', error.response?.data?.message || 'Failed to add module');
    }
  });


  // Update module mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: { name: string; path: string; category: string; permissions: string[] } }) => {
      const token = localStorage.getItem('accessToken') || '';
      return await permissionModuleService.updateModule(id, data, token);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['permission-modules'] });
      showToast('success', 'Module Updated', `${data.name} has been updated successfully`);
      setEditModule({
        name: '',
        path: '',
        category: '',
        hasView: false,
        hasEdit: false,
        hasDelete: false
      });
      setEditingModuleId(null);
    },
    onError: (error: any) => {
      showToast('error', 'Error', error.response?.data?.message || 'Failed to update module');
    }
  });

  // Delete module mutation
  const deleteMutation = useMutation({
    mutationFn: async (moduleId: string) => {
      const token = localStorage.getItem('accessToken') || '';
      return await permissionModuleService.deleteModule(moduleId, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permission-modules'] });
      showDeleteSuccess('Module has been removed successfully.');
    },
    onError: (error: any) => {
      showDeleteError(error.response?.data?.message || 'Failed to delete module');
    }
  });

  const handleEditModule = (module: ModuleConfig) => {
    const permissions = module.permissions;
    const baseKey = module.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

    setEditModule({
      name: module.name,
      path: module.path,
      category: module.category,
      hasView: permissions.some(p => p.includes('view') || p === `${baseKey}_view`),
      hasEdit: permissions.some(p => p.includes('edit') || p === `${baseKey}_edit`),
      hasDelete: permissions.some(p => p.includes('delete') || p === `${baseKey}_delete`)
    });
    setEditingModuleId(module._id || null);
  };

  const handleSaveModule = () => {
    if (!newModule.name || !newModule.path || !newModule.category) {
      showToast('error', 'Validation Error', 'Please fill all required fields');
      return;
    }

    const permissions: string[] = [];
    if (newModule.hasView) permissions.push(generatePermissionKey(newModule.name, 'view'));
    if (newModule.hasEdit) permissions.push(generatePermissionKey(newModule.name, 'edit'));
    if (newModule.hasDelete) permissions.push(generatePermissionKey(newModule.name, 'delete'));

    createMutation.mutate({
      name: newModule.name,
      path: newModule.path,
      category: newModule.category,
      permissions
    });
  };

  const handleUpdateModule = () => {
    if (!editModule.name || !editModule.path || !editModule.category) {
      showToast('error', 'Validation Error', 'Please fill all required fields');
      return;
    }

    const permissions: string[] = [];
    if (editModule.hasView) permissions.push(generatePermissionKey(editModule.name, 'view'));
    if (editModule.hasEdit) permissions.push(generatePermissionKey(editModule.name, 'edit'));
    if (editModule.hasDelete) permissions.push(generatePermissionKey(editModule.name, 'delete'));

    if (editingModuleId) {
      updateMutation.mutate({
        id: editingModuleId,
        data: {
          name: editModule.name,
          path: editModule.path,
          category: editModule.category,
          permissions
        }
      });
    }
  };

  const handleDeleteModule = async (moduleId: string, moduleName: string) => {
    const confirmed = await confirmDeleteDialog({
      title: 'Delete Module?',
      itemName: moduleName,
      text: 'This action cannot be undone. The module will be permanently removed from the system.',
    });

    if (confirmed) {
      deleteMutation.mutate(moduleId);
    }
  };

  // Generate permission key from module name
  const generatePermissionKey = (moduleName: string, type: 'view' | 'edit' | 'delete') => {
    const cleanName = moduleName.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    return `${cleanName}_${type}`;
  };

  const getPermissionBadgeColor = (permCount: number) => {
    if (permCount === 0) return 'bg-green-100 text-green-700 border-green-200';
    if (permCount === 1) return 'bg-blue-100 text-blue-700 border-blue-200';
    return 'bg-purple-100 text-purple-700 border-purple-200';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumbs */}
      <nav className="flex items-center space-x-2 text-sm text-gray-600">
        <Link to="/dashboard" className="flex items-center hover:text-blue-600 transition-colors">
          <Home className="w-4 h-4" />
        </Link>
        <ChevronRight className="w-4 h-4" />
        <span className="text-gray-400">Settings</span>
        <ChevronRight className="w-4 h-4" />
        <span className="text-gray-900 font-semibold">Permissions</span>
      </nav>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">System Modules & Permissions</h1>
          <p className="text-sm text-gray-600 mt-1">
            View access requirements for each system module
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <div className="px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
            <span className="text-sm font-medium text-blue-700">
              {filteredModules.length} Modules
            </span>
          </div>
          <button
            onClick={() => setIsAddingModule(true)}
            className="btn-primary flex items-center text-sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Module
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Total Modules</p>
              <p className="text-3xl font-bold text-gray-900">{allModules.length}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-xl">
              <Shield className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Public Access</p>
              <p className="text-3xl font-bold text-green-600">{allModules.filter(m => m.permissions.length === 0).length}</p>
            </div>
            <div className="p-3 bg-green-50 rounded-xl">
              <Eye className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Protected</p>
              <p className="text-3xl font-bold text-purple-600">{allModules.filter(m => m.permissions.length > 0).length}</p>
            </div>
            <div className="p-3 bg-purple-50 rounded-xl">
              <Lock className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Categories</p>
              <p className="text-3xl font-bold text-orange-600">{categories.length - 1}</p>
            </div>
            <div className="p-3 bg-orange-50 rounded-xl">
              <Settings className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Add Module Form */}
      {isAddingModule && (
        <div className="bg-white rounded-xl border-2 border-primary-200 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-primary-50 to-blue-50 px-6 py-4 border-b border-primary-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <Plus className="w-5 h-5 text-primary-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Add New Module</h2>
              </div>
              <button
                onClick={() => {
                  setIsAddingModule(false);
                  setNewModule({
                    name: '',
                    path: '',
                    category: '',
                    hasView: false,
                    hasEdit: false,
                    hasDelete: false
                  });
                }}
                className="p-2 hover:bg-white/50 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Module Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newModule.name}
                  onChange={(e) => setNewModule({ ...newModule, name: e.target.value })}
                  placeholder="e.g., Report Generator"
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Route Path <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newModule.path}
                  onChange={(e) => setNewModule({ ...newModule, path: e.target.value })}
                  placeholder="e.g., /reports/generator"
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Category <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newModule.category}
                  onChange={(e) => setNewModule({ ...newModule, category: e.target.value })}
                  placeholder="e.g., Reports"
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-blue-900 mb-3">Required Permissions</h4>
              <p className="text-xs text-blue-700 mb-4">
                Select permissions to enable. Keys will be auto-generated as: <code className="bg-blue-100 px-1 rounded">{newModule.name ? generatePermissionKey(newModule.name, 'view') : 'module_name_view'}</code>
              </p>
              <div className="space-y-3">
                <label className="flex items-center space-x-3 cursor-pointer p-3 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={newModule.hasView}
                    onChange={(e) => setNewModule({ ...newModule, hasView: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <Eye className="w-4 h-4 text-blue-600" />
                      <span className="font-medium text-gray-900">View Permission</span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {newModule.name ? generatePermissionKey(newModule.name, 'view') : 'Will be generated from module name'}
                    </span>
                  </div>
                </label>

                <label className="flex items-center space-x-3 cursor-pointer p-3 bg-white border border-green-200 rounded-lg hover:bg-green-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={newModule.hasEdit}
                    onChange={(e) => setNewModule({ ...newModule, hasEdit: e.target.checked })}
                    className="w-4 h-4 text-green-600 rounded focus:ring-2 focus:ring-green-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <Edit2 className="w-4 h-4 text-green-600" />
                      <span className="font-medium text-gray-900">Edit Permission</span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {newModule.name ? generatePermissionKey(newModule.name, 'edit') : 'Will be generated from module name'}
                    </span>
                  </div>
                </label>

                <label className="flex items-center space-x-3 cursor-pointer p-3 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={newModule.hasDelete}
                    onChange={(e) => setNewModule({ ...newModule, hasDelete: e.target.checked })}
                    className="w-4 h-4 text-red-600 rounded focus:ring-2 focus:ring-red-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <Trash2 className="w-4 h-4 text-red-600" />
                      <span className="font-medium text-gray-900">Delete Permission</span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {newModule.name ? generatePermissionKey(newModule.name, 'delete') : 'Will be generated from module name'}
                    </span>
                  </div>
                </label>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-100">
              <button
                onClick={() => {
                  setIsAddingModule(false);
                  setNewModule({
                    name: '',
                    path: '',
                    category: '',
                    hasView: false,
                    hasEdit: false,
                    hasDelete: false
                  });
                }}
                className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveModule}
                className="px-6 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-xl hover:bg-primary-700 flex items-center"
              >
                <Save className="w-4 h-4 mr-2" />
                Add Module
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filter */}
      <div className="flex items-center space-x-4">
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search modules..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
          />
        </div>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all min-w-[200px]"
        >
          {categories.map(cat => (
            <option key={cat} value={cat}>
              {cat === 'all' ? 'All Categories' : cat}
            </option>
          ))}
        </select>
      </div>

      {/* Modules Grid */}
      <div className="grid grid-cols-1 gap-4">
        {filteredModules.map((module) => {
          const Icon = module.icon;
          return (
            <div key={module.path} className="bg-white rounded-xl border border-gray-200 hover:shadow-md transition-all overflow-hidden">
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    {/* Icon */}
                    <div className="p-3 bg-gradient-to-br from-primary-400 to-primary-600 rounded-xl shadow-sm">
                      <Icon className="w-6 h-6 text-white" />
                    </div>

                    {/* Module Info */}
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{module.name}</h3>
                        <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${getPermissionBadgeColor(module.permissions.length)}`}>
                          {module.permissions.length === 0 ? 'Public Access' : `${module.permissions.length} Permission${module.permissions.length > 1 ? 's' : ''} Required`}
                        </span>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center space-x-4 text-sm">
                          <span className="text-gray-500">Category:</span>
                          <span className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded-lg font-medium">
                            {module.category}
                          </span>
                        </div>

                        <div className="flex items-center space-x-4 text-sm">
                          <span className="text-gray-500">Route:</span>
                          <code className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded-lg font-mono text-xs">
                            {module.path}
                          </code>
                        </div>

                        {module.permissions.length > 0 && (
                          <div className="space-y-2">
                            <span className="text-sm text-gray-500">Required Permissions (OR logic - user needs at least one):</span>
                            <div className="flex flex-wrap gap-2">
                              {module.permissions.map(perm => (
                                <span
                                  key={perm}
                                  className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium border border-blue-200"
                                >
                                  <Lock className="w-3 h-3 inline mr-1" />
                                  {PERMISSION_LABELS[perm] || perm}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2 ml-6">
                    {module._id ? (
                      <>
                        {canEdit && (
                          <button
                            onClick={() => handleEditModule(module)}
                            className="p-2.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit module"
                          >
                            <Edit2 className="w-5 h-5" />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => handleDeleteModule(module._id!, module.name)}
                            className="p-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete module"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        )}
                      </>
                    ) : (
                      <button
                        className="p-2.5 text-gray-400 hover:bg-gray-50 rounded-lg transition-colors cursor-not-allowed"
                        title="System module (cannot edit or delete)"
                        disabled
                      >
                        <Lock className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Inline Edit Form */}
              {editingModuleId === module._id && (
                <div className="border-t border-gray-200 bg-gradient-to-b from-blue-50 to-white p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <Edit2 className="w-5 h-5 text-blue-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Edit Module</h3>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Module Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={editModule.name}
                          onChange={(e) => setEditModule({ ...editModule, name: e.target.value })}
                          className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Route Path <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={editModule.path}
                          onChange={(e) => setEditModule({ ...editModule, path: e.target.value })}
                          className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Category <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={editModule.category}
                          onChange={(e) => setEditModule({ ...editModule, category: e.target.value })}
                          className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-blue-900 mb-3">Required Permissions</h4>
                      <p className="text-xs text-blue-700 mb-4">
                        Select permissions to enable. Keys will be: <code className="bg-blue-100 px-1 rounded">{editModule.name ? generatePermissionKey(editModule.name, 'view') : 'module_name_view'}</code>
                      </p>
                      <div className="space-y-3">
                        <label className="flex items-center space-x-3 cursor-pointer p-3 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors">
                          <input
                            type="checkbox"
                            checked={editModule.hasView}
                            onChange={(e) => setEditModule({ ...editModule, hasView: e.target.checked })}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                          />
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <Eye className="w-4 h-4 text-blue-600" />
                              <span className="font-medium text-gray-900">View Permission</span>
                            </div>
                            <span className="text-xs text-gray-500">
                              {editModule.name ? generatePermissionKey(editModule.name, 'view') : 'Will be generated from module name'}
                            </span>
                          </div>
                        </label>

                        <label className="flex items-center space-x-3 cursor-pointer p-3 bg-white border border-green-200 rounded-lg hover:bg-green-50 transition-colors">
                          <input
                            type="checkbox"
                            checked={editModule.hasEdit}
                            onChange={(e) => setEditModule({ ...editModule, hasEdit: e.target.checked })}
                            className="w-4 h-4 text-green-600 rounded focus:ring-2 focus:ring-green-500"
                          />
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <Edit2 className="w-4 h-4 text-green-600" />
                              <span className="font-medium text-gray-900">Edit Permission</span>
                            </div>
                            <span className="text-xs text-gray-500">
                              {editModule.name ? generatePermissionKey(editModule.name, 'edit') : 'Will be generated from module name'}
                            </span>
                          </div>
                        </label>

                        <label className="flex items-center space-x-3 cursor-pointer p-3 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors">
                          <input
                            type="checkbox"
                            checked={editModule.hasDelete}
                            onChange={(e) => setEditModule({ ...editModule, hasDelete: e.target.checked })}
                            className="w-4 h-4 text-red-600 rounded focus:ring-2 focus:ring-red-500"
                          />
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <Trash2 className="w-4 h-4 text-red-600" />
                              <span className="font-medium text-gray-900">Delete Permission</span>
                            </div>
                            <span className="text-xs text-gray-500">
                              {editModule.name ? generatePermissionKey(editModule.name, 'delete') : 'Will be generated from module name'}
                            </span>
                          </div>
                        </label>
                      </div>
                    </div>

                    <div className="flex items-center justify-end space-x-3 pt-4">
                      <button
                        onClick={() => {
                          setEditingModuleId(null);
                          setEditModule({
                            name: '',
                            path: '',
                            category: '',
                            hasView: false,
                            hasEdit: false,
                            hasDelete: false
                          });
                        }}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleUpdateModule}
                        className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 flex items-center"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Update Module
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filteredModules.length === 0 && (
        <div className="bg-white rounded-xl border-2 border-dashed border-gray-300 text-center py-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
            <Search className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No modules found</h3>
          <p className="text-sm text-gray-500">
            Try adjusting your search or filters
          </p>
        </div>
      )}


      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-start space-x-3">
          <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-blue-900 mb-1">How Permissions Work</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• <strong>Public Access:</strong> Anyone logged in can access (no specific permissions required)</li>
              <li>• <strong>OR Logic:</strong> User needs at least ONE of the listed permissions to access</li>
              <li>• <strong>Role-based:</strong> Users get base permissions from their role (Admin/Super Admin)</li>
              <li>• <strong>Custom Permissions:</strong> Super Admin can grant additional permissions to any user</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
